import { getOlxPostRedeemDialogConfig } from "../helpers";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { flattenSessionItems } from "@features/session/helpers/session";
import { useSession } from "@features/session/hooks/useSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import type { ApplyAccessCodeResponse } from "api/commerce/types";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useRouter } from "expo-router";
import { type Dispatch, type SetStateAction, useState } from "react";

type OlxDates = {
	beginStartDate: string;
	endStartDate: string;
	tempAccessCodeResponse?: ApplyAccessCodeResponse;
} | null;

type Props = {
	olxDates: OlxDates;
	setOlxDates: Dispatch<SetStateAction<OlxDates>>;
	handleNext: (result: ApplyAccessCodeResponse) => void;
	toggleShowCollectUserDialog: () => void;
};

const PostRedeemOlxDialog = ({
	olxDates,
	setOlxDates,
	handleNext,
	toggleShowCollectUserDialog,
}: Props) => {
	const [creatingSession, setCreatingSession] = useState(false);

	const updateSession = useSessionStore((state) => state.updateSession);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);

	const { createNewSession } = useSession();

	const {
		currentExamContext: { projectId, organizationId },
	} = useExamContext();

	const router = useRouter();

	const handleStartPracticeTestSession = async (collectionId: number) => {
		try {
			resetSessionStore();
			setCreatingSession(true);

			const params = {
				collectionId: collectionId!,
				projectId,
				organizationId,
				sessionType: "SUMMATIVE" as const,
				sessionSubType: "PRACTICE_TEST" as const,
			} as const;
			const result = await createNewSession(params);
			const flattenedSessionItems = flattenSessionItems(result.sessionItems);

			const newSessionResponse = {
				...result,
				sessionItems: flattenedSessionItems,
			};
			updateSession({
				sessionResponse: newSessionResponse,
				isSummativeTest: true,
				isDiagnostic: false,
			});
		} catch (_error) {
			handleNext(olxDates?.tempAccessCodeResponse!);
		} finally {
			setCreatingSession(false);
		}
	};

	const showOlxDialog = olxDates != null;
	if (showOlxDialog) {
		const examName = olxDates.tempAccessCodeResponse?.data.examName || "";
		const bundleName = olxDates.tempAccessCodeResponse?.data.bundleName || "";

		const config = getOlxPostRedeemDialogConfig(
			olxDates.beginStartDate,
			olxDates.endStartDate,
			examName,
			bundleName,
		);

		return (
			<ConfirmDialog
				visible={showOlxDialog}
				title={config.title}
				body={config.body}
				confirmCta={config.confirmCta}
				dismissCta={config.cancelCta}
				icon="information-outline"
				confirmCtaLoading={creatingSession}
				confirmCallback={async () => {
					const olxCollectionId =
						olxDates?.tempAccessCodeResponse?.data.practiceTestCollection?.[0];
					if (olxCollectionId) {
						await handleStartPracticeTestSession(olxCollectionId);
						toggleShowCollectUserDialog();
					} else {
						router.push("/practice-tests");
					}
					setOlxDates(null);
				}}
				dismissCallback={() => {
					setOlxDates(null);
					router.push("/practice-tests");
				}}
			/>
		);
	}
	return null;
};

export default PostRedeemOlxDialog;
